import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase, uploadFile } from '../lib/supabase';
import { useFleetData } from '../hooks/useFleetData';
import { useOwner } from '../hooks/useOwner';
import { formatDate, formatCurrency, getExpiryStatus, formatMileage } from '../utils/dateUtils';
import { StatusBadge } from '../components/common/Badge';
import { Modal } from '../components/common/Modal';
import { FileUpload } from '../components/common/FileUpload';
import { VehicleForm, STATUS_CONFIG } from './Vehicles';
import { cropStyle } from '../utils/photoCrop';
import { ScanDocument } from '../components/common/ScanDocument';
import { ExpenseModal } from '../components/common/ExpenseModal';
import { ArchivedFiles } from '../components/common/ArchivedFiles';
import { archiveReplacedFile } from '../lib/fileArchive';
import { PHOTO_ALBUMS, albumOf, albumLabel } from '../utils/photoAlbums';
import { Car, Edit2, Shield, Receipt, Wrench, DollarSign, Plus, ExternalLink, Trash2, FileText, Sparkles, Star, Camera, MapPin } from 'lucide-react';
import toast from 'react-hot-toast';
import { latestByDate } from '../utils/recordUtils';

// Where the car is right now (garage, storage unit, customer's home…). Click to change it
// without opening the full edit form — it is the field that changes most often.
const LocationChip = ({ vehicle, canEdit, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(vehicle.location || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const next = value.trim();
    if (next === (vehicle.location || '')) { setEditing(false); return; }
    setSaving(true);
    const { error } = await supabase.from('vehicles').update({ location: next || null }).eq('id', vehicle.id);
    setSaving(false);
    if (error) { toast.error('Could not save the location'); return; }
    onSaved(next || null);
    setEditing(false);
    toast.success('Location updated');
  };

  if (editing) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10 }}>
        <MapPin size={14} style={{ color: 'var(--accent)', flexShrink: 0 }} />
        <input
          className="form-input"
          style={{ maxWidth: 320, padding: '6px 10px', fontSize: 13 }}
          autoFocus
          maxLength={120}
          value={value}
          placeholder="e.g. Limassol garage, bay 3"
          onChange={e => setValue(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') save();
            if (e.key === 'Escape') { setValue(vehicle.location || ''); setEditing(false); }
          }}
        />
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        <button className="btn btn-ghost btn-sm" onClick={() => { setValue(vehicle.location || ''); setEditing(false); }}>Cancel</button>
      </div>
    );
  }

  return (
    <div
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 10, fontSize: 13, color: vehicle.location ? 'var(--text2)' : 'var(--text3)', cursor: canEdit ? 'pointer' : 'default' }}
      onClick={() => { if (canEdit) { setValue(vehicle.location || ''); setEditing(true); } }}
      title={canEdit ? 'Click to change location' : undefined}
    >
      <MapPin size={14} style={{ color: 'var(--accent)' }} />
      {vehicle.location || (canEdit ? 'Set location' : 'No location set')}
      {canEdit && <Edit2 size={11} style={{ opacity: 0.6 }} />}
    </div>
  );
};

// ── Document snippet ────────────────────────────────────────────────────────
// A small inline preview of an uploaded document — an image thumbnail for a
// photo, or the document itself in an iframe for a PDF — instead of making
// the user click a plain link to see what was uploaded.
const isImageUrl = (url) => /\.(png|jpe?g|gif|webp|bmp|heic)(\?|#|$)/i.test(url || '');

const DocumentSnippet = ({ url, height = 160 }) => {
  if (!url) {
    return <div style={{ fontSize: 13, color: 'var(--text3)' }}>No document uploaded yet.</div>;
  }
  // For a PDF, #view=Fit tells the browser's built-in viewer to scale the
  // whole page to fit the frame instead of opening at its native size with
  // scrollbars — this is what actually keeps the full page visible without
  // scrolling; the box height alone can't do that. toolbar/navpanes=0 hides
  // the PDF viewer's own chrome, which would otherwise eat into that space.
  const src = isImageUrl(url) ? url : `${url}${url.includes('#') ? '&' : '#'}toolbar=0&navpanes=0&view=Fit`;
  return (
    <div style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border)', background: 'var(--surface2)', height }}>
      {isImageUrl(url) ? (
        <img src={src} alt="Document preview" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
      ) : (
        <iframe src={src} title="Document preview" style={{ width: '100%', height: '100%', border: 'none' }} />
      )}
      <a href={url} target="_blank" rel="noreferrer"
        style={{ position: 'absolute', top: 8, right: 8, display: 'flex', alignItems: 'center', gap: 4, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)', borderRadius: 20, padding: '4px 10px', fontSize: 11, fontWeight: 700, color: 'white', textDecoration: 'none' }}>
        <ExternalLink size={11} /> Open
      </a>
    </div>
  );
};

const PhotoUpload = ({ vehicle, onUploaded }) => {
  const [uploading, setUploading] = useState(false);
  const primaryPhoto = vehicle.vehicle_photos?.find(p => p.is_primary) || null;
  const [preview, setPreview] = useState(primaryPhoto?.url || null);
  const [deleting, setDeleting] = useState(false);

  // useState above only captures the primary photo once, on mount — it never
  // re-reads it if the vehicle prop changes later (e.g. the Photos tab sets a
  // different photo as primary without a full page reload). Keep it in sync.
  useEffect(() => {
    setPreview(primaryPhoto?.url || null);
  }, [primaryPhoto?.url]);

  // AI photo recognition — a SUGGESTION only. The owner reviews and edits
  // every field before anything is saved; nothing here is auto-applied.
  const [detecting, setDetecting] = useState(false);
  const [suggestion, setSuggestion] = useState(null);   // { make, model, variant, colour, confidence, reasoning }
  const [applying, setApplying] = useState(false);

  const handleDetect = async () => {
    if (!preview) return;
    setDetecting(true);
    setSuggestion(null);
    try {
      const { data, error } = await supabase.functions.invoke('detect-vehicle-photo', {
        body: { photoUrl: preview, currentMake: vehicle.brand, currentModel: vehicle.model },
      });
      if (error || data?.error) {
        toast.error(data?.error || error?.message || 'Could not analyse the photo');
        return;
      }
      setSuggestion({
        make: data.suggestion?.make || '',
        model: data.suggestion?.model || '',
        variant: data.suggestion?.variant || '',
        colour: data.suggestion?.colour || '',
        confidence: data.suggestion?.confidence ?? null,
        reasoning: data.suggestion?.reasoning || '',
      });
    } catch (err) {
      toast.error('Detection failed: ' + err.message);
    }
    setDetecting(false);
  };

  // Only writes the fields the owner has left filled in — never a silent
  // overwrite of make/model/variant with something they cleared or disagreed
  // with. Colour isn't written here: it lives on the vehicle_valuations
  // record (Car Intelligence → Edit Data), not on vehicles, so the owner
  // copies it across themselves — there's no safe "the" valuation record to
  // guess at from here.
  const applySuggestion = async () => {
    if (!suggestion) return;
    const patch = {};
    if (suggestion.make.trim())    patch.brand   = suggestion.make.trim();
    if (suggestion.model.trim())   patch.model   = suggestion.model.trim();
    if (suggestion.variant.trim()) patch.variant = suggestion.variant.trim();
    if (Object.keys(patch).length === 0) {
      toast.error('Nothing to apply — clear a field to skip it, not all of them');
      return;
    }
    setApplying(true);
    try {
      const { error } = await supabase.from('vehicles').update(patch).eq('id', vehicle.id);
      if (error) { toast.error('Failed to update vehicle: ' + error.message); return; }
      toast.success('Vehicle details updated');
      setSuggestion(null);
      onUploaded?.();
    } catch (err) {
      toast.error('Failed to update vehicle: ' + err.message);
    }
    setApplying(false);
  };

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await uploadFile(file, 'vehicles');
      // Replace only the PRIMARY photo record — this used to delete every row
      // for this vehicle_id, which wiped out the whole Photos tab gallery every
      // time the profile photo was changed. Scope the delete to is_primary=true.
      // The old profile photo is kept: it just becomes a normal gallery photo.
      const oldPrimary = vehicle.vehicle_photos?.find(p => p.is_primary);
      if (oldPrimary) {
        await supabase.from('vehicle_photos').update({ is_primary: false }).eq('id', oldPrimary.id);
      }
      await supabase.from('vehicle_photos').insert({
        vehicle_id: vehicle.id,
        url: result.url,
        file_path: result.path,
        is_primary: true,
      });
      setPreview(result.url);
      toast.success('Photo uploaded!');
      onUploaded?.();
    } catch (err) {
      toast.error('Upload failed: ' + err.message);
    }
    setUploading(false);
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this photo?')) return;
    setDeleting(true);
    try {
      // Scoped to the primary photo only — see note above. Deleting the profile
      // photo must never touch the rest of the Photos tab gallery.
      const current = vehicle.vehicle_photos?.find(p => p.is_primary);
      if (current?.file_path) {
        await supabase.storage.from('vehicle-photos').remove([current.file_path]);
      }
      if (current) {
        await supabase.from('vehicle_photos').delete().eq('id', current.id);
      }
      setPreview(null);
      toast.success('Photo deleted');
      onUploaded?.();
    } catch (err) {
      toast.error('Failed to delete photo');
    }
    setDeleting(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
      <div style={{
        width: 80,
        height: 80,
        borderRadius: 12,
        background: 'var(--surface2)',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '2px dashed var(--border)',
        position: 'relative',
      }}>
        {preview
          ? <img src={preview} alt="vehicle" style={{ width: '100%', height: '100%', ...cropStyle(primaryPhoto) }} />
          : <Car size={36} style={{ opacity: 0.3 }} />
        }
      </div>

      {/* Buttons below the photo */}
      <div style={{ display: 'flex', gap: 6 }}>
        {/* Upload / Change button */}
        <label style={{ cursor: 'pointer' }}>
          <span className="btn btn-ghost btn-sm">
            {uploading ? 'Uploading…' : preview ? '↺ Change' : '+ Add Photo'}
          </span>
          <input
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFile}
            disabled={uploading}
          />
        </label>

        {/* Delete button — only show if photo exists */}
        {preview && (
          <button
            className="btn btn-danger btn-sm"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? '…' : '🗑 Delete'}
          </button>
        )}

        {/* AI photo recognition — suggests, never applies on its own */}
        {preview && (
          <button
            className="btn btn-ghost btn-sm"
            onClick={handleDetect}
            disabled={detecting}
            title="Use AI to suggest make, model, variant and colour from this photo"
          >
            <Sparkles size={12} style={{ marginRight: 4 }} />
            {detecting ? 'Analysing…' : 'Detect vehicle details'}
          </button>
        )}
      </div>

      {/* Suggestion review card — every field is editable; nothing saves until
          the owner clicks Apply, and Apply only writes fields left filled in. */}
      {suggestion && (
        <div style={{
          width: '100%', maxWidth: 320, marginTop: 4, padding: '12px 14px',
          borderRadius: 10, border: '1px solid var(--border)', background: 'var(--surface2)',
          textAlign: 'left',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <Sparkles size={13} style={{ color: '#a855f7' }} />
            <span style={{ fontSize: 12, fontWeight: 700 }}>AI suggestion</span>
            {suggestion.confidence != null && (
              <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 'auto' }}>
                {suggestion.confidence}% confidence
              </span>
            )}
          </div>

          {['make', 'model', 'variant', 'colour'].map(field => (
            <div key={field} style={{ marginBottom: 6 }}>
              <label style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text3)' }}>
                {field}
              </label>
              <input
                className="form-input"
                style={{ fontSize: 13, padding: '5px 8px' }}
                value={suggestion[field]}
                onChange={e => setSuggestion(s => ({ ...s, [field]: e.target.value }))}
                placeholder="—"
              />
            </div>
          ))}

          {suggestion.reasoning && (
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4, marginBottom: 10, lineHeight: 1.4 }}>
              {suggestion.reasoning}
            </div>
          )}

          <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 10 }}>
            Colour isn't saved from here — copy it into Car Intelligence → Edit Data → Original Factory Colour.
          </div>

          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn btn-primary btn-sm" onClick={applySuggestion} disabled={applying} style={{ flex: 1 }}>
              {applying ? 'Applying…' : 'Apply make/model/variant'}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => setSuggestion(null)} disabled={applying}>
              Dismiss
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const Section = ({ title, icon: Icon, children, onAdd }) => (
  <div className="card" style={{ marginBottom: 20 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
      <Icon size={16} style={{ color: 'var(--accent)' }} />
      <span style={{ fontWeight: 700 }}>{title}</span>
      {onAdd && <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }} onClick={onAdd}><Plus size={12} /> Add</button>}
    </div>
    {children}
  </div>
);

export const VehicleDetail = () => {
  const { id } = useParams();
  // eslint-disable-next-line no-unused-vars
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [activeTab, setActiveTab] = useState(() => new URLSearchParams(window.location.search).get('tab') || 'overview');

  const titleDeed = useFleetData('title_deeds', id);
  const insurance = useFleetData('insurance_records', id);
  const roadTax = useFleetData('road_tax_records', id);
  const mechanical = useFleetData('mechanical_reports', id);
  const services = useFleetData('service_records', id);
  const expenses = useFleetData('expenses', id);
  const logbook = useFleetData('vehicle_logbook', id);
  const [valuation, setValuation] = useState(null);

  useEffect(() => {
    if (!id) return;
    supabase.from('vehicle_valuations')
      .select('purchase_price, current_value, valuation_comparables')
      .eq('vehicle_id', id).maybeSingle()
      .then(({ data }) => setValuation(data || null));
  }, [id]);
  const [photos, setPhotos]           = useState([]);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [lightboxPhoto, setLightboxPhoto]   = useState(null);
  // Photo albums: which album tab is showing, and which album new uploads go into.
  const [albumFilter, setAlbumFilter]       = useState('all');
  const [uploadAlbum, setUploadAlbum]       = useState('main');

  const uploadPhoto = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setPhotoUploading(true);
    let uploaded = 0;
    for (const file of files) {
      try {
        const ext  = file.name.split('.').pop();
        const path = `${id}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
        const { error: upErr } = await supabase.storage.from('vehicle-photos').upload(path, file);
        if (upErr) throw upErr;
        const { data: { publicUrl } } = supabase.storage.from('vehicle-photos').getPublicUrl(path);
        const { error: insErr } = await supabase.from('vehicle_photos').insert({ vehicle_id: id, url: publicUrl, file_path: path, is_primary: false, album: uploadAlbum });
        if (insErr) throw insErr;
        uploaded++;
      } catch (err) { toast.error(`Failed to upload ${file.name}: ${err.message}`); }
    }
    const { data: photoData } = await supabase.from('vehicle_photos').select('*').eq('vehicle_id', id).order('created_at', { ascending: false });
    setPhotos(photoData || []);
    toast.success(`${uploaded} photo${uploaded !== 1 ? 's' : ''} uploaded!`);
    setPhotoUploading(false);
  };

  // Move a photo to another album (the profile photo stays the profile photo).
  const moveToAlbum = async (photo, album) => {
    const { data, error } = await supabase.from('vehicle_photos').update({ album }).eq('id', photo.id).select('id');
    if (error || !data?.length) { toast.error('Could not move photo' + (error ? ': ' + error.message : '')); return; }
    setPhotos(p => p.map(x => x.id === photo.id ? { ...x, album } : x));
    // A photo that leaves the album being viewed closes the viewer; otherwise just update it.
    if (albumFilter !== 'all' && album !== albumFilter) setLightboxPhoto(null);
    else setLightboxPhoto(p => (p && p.id === photo.id) ? { ...p, album } : p);
    toast.success(`Moved to ${albumLabel(album)}`);
  };

  const deletePhoto = async (photo) => {
    if (!window.confirm('Delete this photo?')) return;
    await supabase.storage.from('vehicle-photos').remove([photo.file_path]);
    await supabase.from('vehicle_photos').delete().eq('id', photo.id);
    setPhotos(p => p.filter(x => x.id !== photo.id));
    toast.success('Photo deleted');
  };

  // Promote a gallery photo to the profile photo shown on the vehicle header
  // and vehicle cards — scoped to is_primary the same way PhotoUpload is, so
  // this never touches the rest of the gallery.
  const setAsProfilePhoto = async (photo) => {
    if (photo.is_primary) return;
    try {
      // Clear is_primary on every OTHER photo for this vehicle in one update —
      // not just whichever row happened to be cached as "the" old primary in
      // local state. That earlier approach could silently leave two rows both
      // flagged primary if that single update failed without being checked,
      // which is exactly why this looked like it worked but didn't stick.
      const { error: clearErr } = await supabase.from('vehicle_photos')
        .update({ is_primary: false })
        .eq('vehicle_id', id)
        .neq('id', photo.id);
      if (clearErr) throw clearErr;

      // .select() here so we can tell "updated 0 rows" (RLS silently blocked
      // it — no error, just no effect) apart from a real success. Supabase
      // does not throw on a permission-filtered update, so without checking
      // the returned rows this failure mode looks identical to success.
      const { data: setData, error: setErr } = await supabase.from('vehicle_photos')
        .update({ is_primary: true })
        .eq('id', photo.id)
        .select('id');
      if (setErr) throw setErr;
      if (!setData || setData.length === 0) {
        throw new Error('Update was blocked (0 rows changed) — this is almost always a database permissions (RLS) issue, not a bug in the page itself.');
      }

      // Re-fetch from the DB instead of trusting optimistic local state, so
      // the UI always reflects what's actually stored — and so this call
      // self-heals if an earlier attempt ever left more than one row primary.
      const { data: freshPhotos, error: fetchErr } = await supabase.from('vehicle_photos')
        .select('*').eq('vehicle_id', id).order('created_at', { ascending: false });
      if (fetchErr) throw fetchErr;
      setPhotos(freshPhotos || []);
      setVehicle(prev => prev ? { ...prev, vehicle_photos: freshPhotos || [] } : prev);
      toast.success('Profile photo updated');
    } catch (err) {
      toast.error('Failed to set profile photo: ' + err.message);
    }
  };

  // Crop for a photo: focal_x/focal_y (CSS object-position, 0–100%) pan
  // within whatever overflow object-fit:cover already produces, and zoom
  // (a CSS transform: scale(), 1–3x) magnifies around that same point — for
  // a photo where the car is small/far away, panning alone has nothing to
  // work with, so zoom is what actually makes it fill the crop.
  const updateCrop = async (photo, patch) => {
    const { error } = await supabase.from('vehicle_photos')
      .update(patch)
      .eq('id', photo.id);
    if (error) { toast.error('Failed to save crop: ' + error.message); return; }
    setPhotos(p => p.map(x => x.id === photo.id ? { ...x, ...patch } : x));
    setVehicle(prev => prev ? { ...prev, vehicle_photos: (prev.vehicle_photos || []).map(x => x.id === photo.id ? { ...x, ...patch } : x) } : prev);
    setLightboxPhoto(p => (p && p.id === photo.id) ? { ...p, ...patch } : p);
  };

  // Gallery navigation — step to the previous/next photo of THIS car while
  // the lightbox is open, wrapping around at either end.
  // Stepping stays inside the album currently being viewed.
  const visiblePhotos = albumFilter === 'all' ? photos : photos.filter(p => albumOf(p) === albumFilter);
  const albumCount = (key) => photos.filter(p => albumOf(p) === key).length;
  const lightboxIndex = lightboxPhoto ? visiblePhotos.findIndex(p => p.id === lightboxPhoto.id) : -1;
  const showPhotoAt = (idx) => {
    if (!visiblePhotos.length) return;
    setLightboxPhoto(visiblePhotos[(idx + visiblePhotos.length) % visiblePhotos.length]);
  };

  useEffect(() => {
    if (!lightboxPhoto) return;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') showPhotoAt(lightboxIndex - 1);
      else if (e.key === 'ArrowRight') showPhotoAt(lightboxIndex + 1);
      else if (e.key === 'Escape') setLightboxPhoto(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxPhoto, lightboxIndex, photos]); // eslint-disable-line react-hooks/exhaustive-deps

  // Modals
  const [tdModal, setTdModal] = useState(false);
  const [editTdRecord, setEditTdRecord] = useState(null);
  const [insModal, setInsModal] = useState(false);
  const [editInsRecord, setEditInsRecord] = useState(null);
  const [rtModal, setRtModal] = useState(false);
  const [editRtRecord, setEditRtRecord] = useState(null);
  const [mechModal, setMechModal] = useState(false);
  const [editMechRecord, setEditMechRecord] = useState(null);
  const [svcModal, setSvcModal] = useState(false);
  const [editSvcRecord, setEditSvcRecord] = useState(null);
  const [expModal, setExpModal] = useState(false);
  const [editExpRecord, setEditExpRecord] = useState(null);
  const [expCategory, setExpCategory] = useState(null);
  const { role, isOwner, isAdmin } = useOwner();
  const canEdit   = isOwner || isAdmin || role === 'editor';  // viewers are read-only
  const canDelete = isOwner || isAdmin;                        // editors cannot delete
  const [logModal, setLogModal] = useState(false);
  const [editLogRecord, setEditLogRecord] = useState(null);
  const [scanModal, setScanModal] = useState(false);

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase.from('vehicles').select('*, vehicle_photos(*)').eq('id', id).single();
      setVehicle(data);
      // Fetch photos
      const { data: photoData } = await supabase
        .from('vehicle_photos')
        .select('*')
        .eq('vehicle_id', id)
        .order('created_at', { ascending: false });
      setPhotos(photoData || []);
      setLoading(false);
    };
    load();
  }, [id]);

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;
  if (!vehicle) return <div className="page"><p>Vehicle not found.</p></div>;

  // Summary cards show the record that is valid the longest, not the one entered last.
  const latestIns = latestByDate(insurance.data, 'expiry_date');
  const latestRt = latestByDate(roadTax.data, 'expiry_date');
  const latestMech = latestByDate(mechanical.data, 'next_due_date');
  const latestSvc = services.data[0];
  // An immobilised vehicle is off the road, so it doesn't need road tax or an
  // MOT/mechanical inspection — neither should read as "missing" for it.
  const isImmobilized = vehicle?.is_immobilized || vehicle?.status === 'immobilized';
  // Classic cars must carry an immobilization document, whether or not the car
  // is currently immobilised, so they always get the Immobilization tab.
  const isClassic = vehicle?.status === 'classic';

  // ── Overview figures ──────────────────────────────────────────────────────
  // Restoration is capitalised: excluded from running costs, added to invested
  // capital instead — consistent with Portfolio and Fleet Insights.
  const restorationSpend = expenses.data
    .filter(r => r.category === 'Restoration')
    .reduce((sum, r) => sum + Number(r.amount || 0), 0);

  const runningCosts = [
    ...expenses.data.filter(r => r.category !== 'Restoration').map(r => Number(r.amount || 0)),
    ...insurance.data.map(r => Number(r.cost || 0)),
    ...roadTax.data.map(r => Number(r.amount_paid || 0)),
    ...mechanical.data.map(r => Number(r.cost || 0)),
    ...services.data.map(r => Number(r.cost || 0)),
  ].reduce((a, b) => a + b, 0);

  const maintenanceCosts = [
    ...expenses.data.filter(r => ['Repairs','Parts','Tyres','Service'].includes(r.category)).map(r => Number(r.amount || 0)),
    ...mechanical.data.map(r => Number(r.cost || 0)),
    ...services.data.map(r => Number(r.cost || 0)),
  ].reduce((a, b) => a + b, 0);

  const vehicleKm = Number(vehicle?.mileage) || 0;
  const costPerKm = vehicleKm > 0 && maintenanceCosts > 0 ? maintenanceCosts / vehicleKm : null;

  const purchasePrice = valuation?.purchase_price ? Number(valuation.purchase_price) : null;
  const marketValue   = valuation?.current_value ? Number(valuation.current_value) : null;
  const investedTotal = purchasePrice !== null ? purchasePrice + restorationSpend : null;
  const gain    = marketValue !== null && investedTotal !== null ? marketValue - investedTotal : null;
  const gainPct = gain !== null && investedTotal > 0 ? (gain / investedTotal) * 100 : null;
  // One return figure across the app: value now vs EVERYTHING put into the car
  // (purchase + restoration + running costs). Same method as Portfolio and Car Intelligence.
  const netReturn    = gain !== null ? gain - runningCosts : null;
  const returnBase   = investedTotal !== null ? investedTotal + runningCosts : null;
  const returnPct    = netReturn !== null && returnBase > 0 ? (netReturn / returnBase) * 100 : null;
  const valueUnbacked = marketValue !== null && valuation?.valuation_comparables === 0;
  // Restoration is capitalised (it raises invested capital in Portfolio), so it is
  // deliberately excluded from running-cost totals and the All Expenses ledger.
  const runningExpenses = expenses.data.filter(r => r.category !== 'Restoration');
  const totalExpenses = [
    ...runningExpenses.map(r => Number(r.amount || 0)),
    ...insurance.data.map(r => Number(r.cost || 0)),
    ...roadTax.data.map(r => Number(r.amount_paid || 0)),
    ...mechanical.data.map(r => Number(r.cost || 0)),
    ...services.data.map(r => Number(r.cost || 0)),
  ].reduce((sum, amount) => sum + amount, 0);

  const reloadVehicle = async () => {
    const { data } = await supabase.from('vehicles').select('*, vehicle_photos(*)').eq('id', id).single();
    if (data) setVehicle(data);
  };

  const saveVehicleDoc = async (urlField, pathField, result, slot, label) => {
    const oldUrl = vehicle?.[urlField];
    const oldPath = vehicle?.[pathField];
    const { error } = await supabase
      .from('vehicles')
      .update({ [urlField]: result.url, [pathField]: result.path })
      .eq('id', id);
    if (error) { toast.error('Could not save document: ' + error.message); return; }
    // Keep the document that was just replaced under "Previous versions".
    await archiveReplacedFile({ vehicleId: id, slot, oldUrl, oldPath, newUrl: result.url, label });
    toast.success('Document saved');
    reloadVehicle();
  };

  const TAB_CONFIG = [
    {
      key: 'overview',
      label: 'Overview',
      icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>,
    },
    ...(vehicle?.status === 'classic' ? [{
      key: 'classic-id',
      label: 'Classic ID',
      icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M7 9h6M7 13h10"/><circle cx="17" cy="8" r="1.5"/></svg>,
    }] : []),
    ...(isImmobilized || isClassic ? [{
      key: 'immobilization',
      label: 'Immobilization',
      icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><line x1="5" y1="5" x2="19" y2="19"/></svg>,
    }] : []),
    {
      key: 'title-deed',
      label: 'Title Deed',
      icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="13" y2="17"/></svg>,
    },
    {
      key: 'insurance',
      label: 'Insurance',
      icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
    },
    {
      key: 'road-tax',
      label: 'Road Tax',
      icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>,
    },
    {
      key: 'mechanical',
      label: 'Mechanical Inspection',
      icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>,
    },
    {
      key: 'services',
      label: 'Services',
      icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/></svg>,
    },
    {
      key: 'expenses',
      label: 'Expenses',
      icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
    },
    {
      key: 'restoration',
      label: 'Restoration',
      icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>,
    },
    {
      key: 'photos',
      label: 'Photos',
      icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>,
    },
    {
      key: 'logbook',
      label: 'Logbook',
      icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>,
    },
  ];

  return (
    <div className="page">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20, marginBottom: 24, flexWrap: 'wrap' }}>
        <PhotoUpload vehicle={vehicle} onUploaded={() => window.location.reload()} />
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: 'DM Mono,monospace', color: 'var(--accent)', fontSize: 13 }}>{vehicle.registration_number}</div>
          <h1 style={{ fontSize: 24, fontWeight: 800, letterSpacing: -0.5, margin: '4px 0 8px' }}>{vehicle.brand} {vehicle.model} <span style={{ color: 'var(--text3)', fontWeight: 400 }}>· {vehicle.year}</span></h1>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <span className="badge badge-blue">{vehicle.fuel_type}</span>
            <span className="badge badge-gray">{vehicle.ownership_status}</span>
            <span className={`badge badge-${vehicle.status === 'active' ? 'green' : 'gray'}`}>{STATUS_CONFIG[vehicle.status]?.label || vehicle.status}</span>
            {vehicle.color && <span className="badge badge-gray">{vehicle.color}</span>}
          </div>
          <LocationChip
            key={`${vehicle.id}-${vehicle.location || ''}`}
            vehicle={vehicle}
            canEdit={canEdit}
            onSaved={loc => setVehicle(prev => prev ? { ...prev, location: loc } : prev)}
          />
        </div>
        <button className="btn btn-ghost" onClick={() => setEditing(true)}><Edit2 size={14} /> Edit</button>
        {canEdit && <button className="btn btn-ghost" onClick={() => setScanModal(true)}><Camera size={14} /> Scan Document</button>}
        <button className="btn btn-ghost" onClick={() => window.open(`/vehicles/${id}/passport`, '_blank')}><FileText size={14} /> Passport</button>
        <button className="btn btn-ghost" onClick={() => navigate(`/forms?vehicle=${id}`)}><FileText size={14} /> Sale forms</button>
      </div>

      {/* Expiry Quick-View */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'Insurance', date: latestIns?.expiry_date },
          { label: 'Road Tax', date: latestRt?.expiry_date },
          { label: 'Mechanical', date: latestMech?.next_due_date },
          { label: 'Next Service', date: latestSvc?.next_service_date },
        ].map(item => {
          // eslint-disable-next-line no-unused-vars
          const s = getExpiryStatus(item.date);
          return (
            <div key={item.label} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 4 }}>{item.label}</div>
              <StatusBadge date={item.date} />
              {item.date && <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 4 }}>{formatDate(item.date)}</div>}
            </div>
          );
        })}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px' }}>
          <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 4 }}>Total Expenses</div>
          <div style={{ fontWeight: 700, color: 'var(--green)' }}>{formatCurrency(totalExpenses)}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        {TAB_CONFIG.map(t => (
          <div key={t.key} className={`tab${activeTab === t.key ? ' active' : ''}`} onClick={() => setActiveTab(t.key)}>
            {t.icon}{t.label}
          </div>
        ))}
      </div>

      {activeTab === 'overview' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* ── Financial ── */}
          <div className="card">
            <OvHead title="Financial" hint="Valuation, invested capital and running costs"
              actionLabel="Car Intelligence →" onAction={() => navigate('/classic-car-intelligence')} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(165px, 1fr))', gap: 10 }}>
              <OvMetric label="Estimated Market Value"
                value={marketValue !== null ? formatCurrency(marketValue) : '—'}
                tone={marketValue !== null ? '#22c55e' : undefined}
                note={valueUnbacked ? '⚠ no comparables' : marketValue === null ? 'not valued yet' : null}
                noteTone={valueUnbacked ? '#f97316' : undefined} />
              <OvMetric label="Invested"
                value={investedTotal !== null ? formatCurrency(investedTotal) : '—'}
                note={restorationSpend > 0 ? `incl. ${formatCurrency(restorationSpend)} restoration` : null} />
              <OvMetric label={netReturn !== null && netReturn < 0 ? 'Net Loss' : 'Net Return'}
                value={netReturn === null ? '—' : `${netReturn >= 0 ? '+' : ''}${formatCurrency(Math.abs(netReturn))}`}
                tone={netReturn === null ? undefined : netReturn >= 0 ? '#22c55e' : '#ef4444'}
                note={returnPct === null ? null : `${returnPct >= 0 ? '+' : ''}${returnPct.toFixed(1)}% after running costs`}
                noteTone={netReturn === null ? undefined : netReturn >= 0 ? '#22c55e' : '#ef4444'} />
              <OvMetric label="Running Costs" value={formatCurrency(runningCosts)} tone="#f97316"
                note="excludes restoration" />
              <OvMetric label="Maintenance / km"
                value={costPerKm !== null ? `${formatCurrency(costPerKm)}/km` : '—'}
                note={costPerKm !== null ? `${formatCurrency(maintenanceCosts)} total` : 'needs mileage'} />
            </div>
          </div>

          {/* ── Compliance ── */}
          <div className="card">
            <OvHead title="Compliance" hint="Click any card to open its records" />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(205px, 1fr))', gap: 10 }}>
              {[
                { label: 'Insurance', date: latestIns?.expiry_date, sub: latestIns?.insurance_company, tab: 'insurance', docMissing: !latestIns?.document_url },
                { label: 'Road Tax', date: latestRt?.expiry_date, sub: latestRt?.amount_paid ? formatCurrency(latestRt.amount_paid) : null, tab: 'road-tax', docMissing: !latestRt?.receipt_url, exemptWhenImmobilized: true },
                { label: 'MOT / Mechanical Inspection', date: latestMech?.next_due_date, sub: latestMech?.garage_name, tab: 'mechanical', docMissing: !latestMech?.document_url, exemptWhenImmobilized: true },
                { label: 'Next Service', date: latestSvc?.next_service_date, sub: latestSvc?.garage_name, tab: 'services' },
                ...(isClassic ? [{ label: 'Immobilization Document', tab: 'immobilization', docOnly: true, hasDoc: !!vehicle.immobilization_doc_url }] : []),
              ].map(c => {
                if (c.docOnly) {
                  const tone = c.hasDoc ? '#22c55e' : '#f97316';
                  return (
                    <div key={c.label} onClick={() => setActiveTab(c.tab)}
                      style={{ position: 'relative', background: 'var(--surface2)', borderRadius: 10, padding: '12px 14px', cursor: 'pointer',
                               border: `1px solid ${tone}55`, transition: 'var(--transition)' }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = tone}
                      onMouseLeave={e => e.currentTarget.style.borderColor = tone + '55'}>
                      <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{c.label}</div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: tone }}>{c.hasDoc ? 'On file' : 'Missing'}</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>{c.hasDoc ? 'Click to view' : 'Required for classic cars'}</div>
                    </div>
                  );
                }
                // Immobilised vehicles are off the road, so an unrecorded road
                // tax / MOT isn't a compliance gap for them — it's expected.
                const exempt = c.exemptWhenImmobilized && isImmobilized && !c.date;
                const docMissing = exempt ? false : c.docMissing;
                const st = c.date ? getExpiryStatus(c.date) : null;
                const tone = exempt ? 'var(--text3)'
                  : !c.date ? 'var(--text3)'
                  : ['expired','critical'].includes(st.status) ? '#ef4444'
                  : st.status === 'warning' ? '#f97316'
                  : st.status === 'soon' ? '#eab308' : '#22c55e';
                return (
                  <div key={c.label} onClick={() => setActiveTab(c.tab)}
                    style={{ position: 'relative', background: 'var(--surface2)', borderRadius: 10, padding: '12px 14px', cursor: 'pointer',
                             border: `1px solid ${c.date ? tone + '55' : 'var(--border)'}`, transition: 'var(--transition)' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = tone}
                    onMouseLeave={e => e.currentTarget.style.borderColor = c.date ? tone + '55' : 'var(--border)'}>
                    {docMissing && (
                      <span
                        className="missing-doc-dot"
                        title={`No ${c.label.toLowerCase()} document uploaded yet`}
                      />
                    )}
                    <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{c.label}</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: tone }}>{exempt ? 'Not required' : c.date ? formatDate(c.date) : 'Not recorded'}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {exempt ? 'Vehicle is immobilised' : c.date ? (st.status === 'expired' ? `Expired ${Math.abs(st.days)}d ago` : `${st.days} days left`) : 'Add a record'}
                      {!exempt && c.sub ? ` · ${c.sub}` : ''}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Specification ── */}
          <div className="card">
            <OvHead title="Specification" actionLabel="Edit →" onAction={() => setEditing(true)} />
            <div className="form-grid">
              {[
                ['Registration', vehicle.registration_number],
                ['Make', vehicle.brand], ['Model', vehicle.model], ['Variant', vehicle.variant],
                ['Year', vehicle.year], ['Body Style', vehicle.body_style],
                ['Exterior Colour', vehicle.color], ['Interior Colour', vehicle.interior_colour],
                ['Engine Size', vehicle.engine_size], ['Transmission', vehicle.transmission],
                ['Drive Side', vehicle.drive_side], ['Fuel Type', vehicle.fuel_type],
                ['Mileage', vehicle.mileage ? formatMileage(vehicle.mileage) : '—'],
                ['VIN', vehicle.vin], ['Engine Number', vehicle.engine_number],
                ['Ownership', vehicle.ownership_status],
                ['Location', vehicle.location],
              ].map(([k, v]) => (
                <div key={k}>
                  <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 3 }}>{k}</div>
                  <div style={{ fontWeight: 500, fontSize: 14 }}>{v || '—'}</div>
                </div>
              ))}
            </div>
            {vehicle.notes && (
              <div style={{ marginTop: 16, padding: 12, background: 'var(--surface2)', borderRadius: 8, fontSize: 13, color: 'var(--text2)' }}>
                {vehicle.notes}
              </div>
            )}
          </div>

          {/* ── Recent activity ── */}
          <div className="card">
            <OvHead title="Recent Activity" hint="Latest costs and logbook entries" />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20 }}>
              <OvList title="Latest expenses" onSeeAll={() => setActiveTab('expenses')}
                empty="No expenses recorded"
                rows={expenses.data.filter(r => r.category !== 'Restoration').slice(0, 4).map(r => ({
                  key: r.id,
                  left: r.description || r.category || 'Expense',
                  sub: formatDate(r.expense_date),
                  right: formatCurrency(Number(r.amount || 0)),
                }))} />
              <OvList title="Logbook" onSeeAll={() => setActiveTab('logbook')}
                empty="No logbook entries"
                rows={logbook.data.slice(0, 4).map(e => ({
                  key: e.id,
                  left: e.entry_type || 'Entry',
                  sub: formatDate(e.entry_date),
                  right: e.mileage ? formatMileage(e.mileage, { both: false }) : '',
                }))} />
            </div>
          </div>

        </div>
      )}

      {activeTab === 'classic-id' && (
        <div className="card">
          <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 6 }}>Classic ID Certificate</div>
          <div style={{ fontWeight: 700, fontSize: 22, letterSpacing: 0.5 }}>{vehicle.classic_id || '—'}</div>
          {!vehicle.classic_id && (
            <div style={{ marginTop: 10, fontSize: 13, color: '#f97316' }}>
              No Classic ID number recorded yet — edit the vehicle to add it.
            </div>
          )}
          <div style={{ marginTop: 20 }}>
            <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 8 }}>Certificate document</div>
            <div style={{ marginBottom: 12 }}>
              <DocumentSnippet url={vehicle.classic_id_doc_url} height={640} />
            </div>
            <FileUpload
              folder="classic-id"
              label={vehicle.classic_id_doc_url ? 'Upload new version (PDF/JPG/PNG) — the old one is kept' : 'Upload certificate (PDF/JPG/PNG)'}
              onUploaded={(r) => saveVehicleDoc('classic_id_doc_url', 'classic_id_doc_path', r, 'classic_id', 'Classic ID certificate')}
            />
            <ArchivedFiles
              vehicleId={id}
              slot="classic_id"
              current={{ url: vehicle.classic_id_doc_url, path: vehicle.classic_id_doc_path }}
              target={{ table: 'vehicles', rowId: id, urlField: 'classic_id_doc_url', pathField: 'classic_id_doc_path' }}
              onChanged={reloadVehicle}
            />
          </div>
        </div>
      )}

      {activeTab === 'title-deed' && (
        <Section title="Title Deed" icon={FileText} onAdd={canEdit ? () => setTdModal(true) : undefined}>
          {titleDeed.data.length === 0 ? (
            <div className="empty-state"><FileText /><h3>No title deed recorded</h3><p>Add the ownership document for this vehicle.</p></div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {titleDeed.data.map(r => (
                <div key={r.id} style={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 260px) 1fr auto', gap: 16, alignItems: 'start', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 10, padding: 14 }}>
                  <DocumentSnippet url={r.document_url} height={130} />
                  <div>
                    <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 2 }}>Registered Owner</div>
                    <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 10 }}>{r.owner_name || '—'}</div>
                    <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 2 }}>Issue Date</div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{r.issue_date ? formatDate(r.issue_date) : '—'}</div>
                    {r.notes && (
                      <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.5, marginTop: 10 }}>{r.notes}</div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {canEdit && (
                      <button className="btn btn-ghost btn-sm" onClick={() => { setEditTdRecord(r); setTdModal(true); }}>
                        <Edit2 size={12} />
                      </button>
                    )}
                    {canDelete && (
                      <button className="btn btn-danger btn-sm" onClick={async () => {
                        if (!window.confirm('Delete this title deed record?')) return;
                        await supabase.from('title_deeds').delete().eq('id', r.id);
                        toast.success('Deleted');
                        titleDeed.refetch();
                      }}>
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>
      )}

      {activeTab === 'immobilization' && (
        <div className="card">
          <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 6 }}>Immobilization</div>
          {isImmobilized ? (
            <>
              <div style={{ fontWeight: 600, fontSize: 16, color: '#ef4444', marginBottom: 4 }}>This vehicle is marked immobilized</div>
              <div style={{ fontSize: 13, color: 'var(--text3)' }}>Road tax alerts are suppressed while immobilized.</div>
            </>
          ) : (
            <>
              <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 4 }}>Immobilization document required</div>
              <div style={{ fontSize: 13, color: 'var(--text3)' }}>Classic cars must have an immobilization document on file.</div>
            </>
          )}
          {isClassic && !vehicle.immobilization_doc_url && (
            <div style={{ marginTop: 10, fontSize: 13, color: '#f97316' }}>
              No immobilization document uploaded yet — please upload it below.
            </div>
          )}
          <div style={{ marginTop: 20 }}>
            <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 8 }}>Immobilization document</div>
            {vehicle.immobilization_doc_url ? (
              <a href={vehicle.immobilization_doc_url} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm" style={{ marginBottom: 10 }}>
                <ExternalLink size={12} /> View current document
              </a>
            ) : (
              <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 10 }}>No document uploaded yet.</div>
            )}
            <FileUpload
              folder="immobilization"
              label={vehicle.immobilization_doc_url ? 'Upload new version (PDF/JPG/PNG) — the old one is kept' : 'Upload document (PDF/JPG/PNG)'}
              onUploaded={(r) => saveVehicleDoc('immobilization_doc_url', 'immobilization_doc_path', r, 'immobilization', 'Immobilization document')}
            />
            <ArchivedFiles
              vehicleId={id}
              slot="immobilization"
              current={{ url: vehicle.immobilization_doc_url, path: vehicle.immobilization_doc_path }}
              target={{ table: 'vehicles', rowId: id, urlField: 'immobilization_doc_url', pathField: 'immobilization_doc_path' }}
              onChanged={reloadVehicle}
            />
          </div>
        </div>
      )}

      {activeTab === 'insurance' && (
        <Section title="Insurance Records" icon={Shield} onAdd={canEdit ? () => setInsModal(true) : undefined}>
          {insurance.data.length === 0 ? <div className="empty-state"><Shield /><h3>No insurance records</h3></div> : (
            <div className="table-wrap"><table>
              <thead><tr><th>Company</th><th>Policy #</th><th>Type</th><th>Insured Amount</th><th>Start</th><th>Expiry</th><th>Cost</th><th>Status</th><th></th></tr></thead>
              <tbody>{insurance.data.map(r => (
                <tr key={r.id}>
                  <td className="td-primary">{r.insurance_company}</td>
                  <td style={{ fontFamily: 'DM Mono,monospace', fontSize: 12 }}>{r.policy_number}</td>
                  <td>
                    <span className={`badge badge-${r.insurance_type === 'Full Comprehensive' ? 'green' : r.insurance_type === 'Third Party' ? 'blue' : 'gray'}`}>
                      {r.insurance_type || '—'}
                    </span>
                  </td>
                  <td>{r.insurance_type === 'Full Comprehensive' && r.insured_amount ? formatCurrency(r.insured_amount) : '—'}</td>
                  <td>{formatDate(r.start_date)}</td>
                  <td>{formatDate(r.expiry_date)}</td>
                  <td>{formatCurrency(r.cost)}</td>
                  <td><StatusBadge date={r.expiry_date} /></td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {r.document_url && (
                        <a href={r.document_url} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm">
                          <ExternalLink size={12} />
                        </a>
                      )}
                      {canEdit && (
                        <button className="btn btn-ghost btn-sm" onClick={() => {
                          setEditInsRecord(r);
                          setInsModal(true);
                        }}>
                          <Edit2 size={12} />
                        </button>
                      )}
                      {canDelete && (
                        <button className="btn btn-danger btn-sm" onClick={async () => {
                          if (!window.confirm('Delete this record?')) return;
                          await supabase.from('insurance_records').delete().eq('id', r.id);
                          toast.success('Deleted');
                          insurance.refetch();
                        }}>
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}</tbody>
            </table></div>
          )}
        </Section>
      )}

      {activeTab === 'road-tax' && (
        <Section title="Road Tax Records" icon={Receipt} onAdd={canEdit ? () => setRtModal(true) : undefined}>
          {roadTax.data.length === 0 ? <div className="empty-state"><Receipt /><h3>No road tax records</h3></div> : (
            <div className="table-wrap"><table>
              <thead><tr><th>Payment Date</th><th>Expiry</th><th>Amount</th><th>Status</th><th></th></tr></thead>
              <tbody>{roadTax.data.map(r => (
                <tr key={r.id}>
                  <td>{formatDate(r.payment_date)}</td>
                  <td>{formatDate(r.expiry_date)}</td>
                  <td>{formatCurrency(r.amount_paid)}</td>
                  <td><StatusBadge date={r.expiry_date} /></td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {r.receipt_url && (
                        <a href={r.receipt_url} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm">
                          <ExternalLink size={12} />
                        </a>
                      )}
                      {canEdit && (
                        <button className="btn btn-ghost btn-sm" onClick={() => {
                          setEditRtRecord(r);
                          setRtModal(true);
                        }}>
                          <Edit2 size={12} />
                        </button>
                      )}
                      {canDelete && (
                        <button className="btn btn-danger btn-sm" onClick={async () => {
                          if (!window.confirm('Delete this record?')) return;
                          await supabase.from('road_tax_records').delete().eq('id', r.id);
                          toast.success('Deleted');
                          roadTax.refetch();
                        }}>
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}</tbody>
            </table></div>
          )}
        </Section>
      )}

      {activeTab === 'mechanical' && (
        <Section title="Mechanical Reports" icon={Wrench} onAdd={canEdit ? () => setMechModal(true) : undefined}>
          {mechanical.data.length === 0 ? <div className="empty-state"><Wrench /><h3>No mechanical reports</h3></div> : (
            <div className="table-wrap"><table>
              <thead><tr><th>Date</th><th>Garage</th><th>Result</th><th>Cost</th><th>Next Due</th><th>Status</th><th></th></tr></thead>
              <tbody>{mechanical.data.map(r => (
                <tr key={r.id}>
                  <td>{formatDate(r.report_date)}</td>
                  <td className="td-primary">{r.garage_name}</td>
                  <td><span className={`badge badge-${r.result === 'Passed' ? 'green' : r.result === 'Failed' ? 'red' : 'orange'}`}>{r.result}</span></td>
                  <td>{r.cost ? formatCurrency(r.cost) : '—'}</td>
                  <td>{formatDate(r.next_due_date)}</td>
                  <td><StatusBadge date={r.next_due_date} /></td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {r.document_url && (
                        <a href={r.document_url} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm">
                          <ExternalLink size={12} />
                        </a>
                      )}
                      {canEdit && (
                        <button className="btn btn-ghost btn-sm" onClick={() => {
                          setEditMechRecord(r);
                          setMechModal(true);
                        }}>
                          <Edit2 size={12} />
                        </button>
                      )}
                      {canDelete && (
                        <button className="btn btn-danger btn-sm" onClick={async () => {
                          if (!window.confirm('Delete this record?')) return;
                          await supabase.from('mechanical_reports').delete().eq('id', r.id);
                          toast.success('Deleted');
                          mechanical.refetch();
                        }}>
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}</tbody>
            </table></div>
          )}
        </Section>
      )}

      {activeTab === 'services' && (
        <Section title="Service History" icon={Wrench} onAdd={canEdit ? () => setSvcModal(true) : undefined}>
          {services.data.length === 0 ? <div className="empty-state"><Wrench /><h3>No service records</h3></div> : (
            <div className="table-wrap"><table>
              <thead><tr><th>Date</th><th>Garage</th><th>Mileage</th><th>Cost</th><th>Next Service</th><th></th></tr></thead>
              <tbody>{services.data.map(r => (
                <tr key={r.id}>
                  <td>{formatDate(r.service_date)}</td>
                  <td className="td-primary">{r.garage_name}</td>
                  <td>{r.mileage_at_service?.toLocaleString()} km</td>
                  <td>{formatCurrency(r.cost)}</td>
                  <td><StatusBadge date={r.next_service_date} /></td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {r.invoice_url && (
                        <a href={r.invoice_url} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm">
                          <ExternalLink size={12} />
                        </a>
                      )}
                      {canEdit && (
                        <button className="btn btn-ghost btn-sm" onClick={() => {
                          setEditSvcRecord(r);
                          setSvcModal(true);
                        }}>
                          <Edit2 size={12} />
                        </button>
                      )}
                      {canDelete && (
                        <button className="btn btn-danger btn-sm" onClick={async () => {
                          if (!window.confirm('Delete this record?')) return;
                          await supabase.from('service_records').delete().eq('id', r.id);
                          toast.success('Deleted');
                          services.refetch();
                        }}>
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}</tbody>
            </table></div>
          )}
        </Section>
      )}

      {activeTab === 'logbook' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ fontSize: 13, color: 'var(--text2)' }}>{logbook.data.length} log entries</div>
            <button className="btn btn-primary btn-sm" onClick={() => setLogModal(true)}>+ Add Entry</button>
          </div>

          {logbook.data.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '48px 20px' }}>
              <p style={{ color: 'var(--text3)', fontSize: 13 }}>No logbook entries yet. Track mileage, trips, driver assignments and storage location here.</p>
            </div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--surface2)' }}>
                    {['Date','Type','Mileage','Driver','From → To','Purpose','Status',''].map(h => (
                      <th key={h} style={{ padding: '10px 14px', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text3)', textAlign: 'left', borderBottom: '1px solid var(--border)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {logbook.data.map(entry => {
                    const isOverdue = entry.expected_return && !entry.actual_return && new Date(entry.expected_return) < new Date() && entry.status !== 'Completed' && entry.status !== 'Cancelled';
                    const missingPhoto = entry.entry_type === 'Mileage' && entry.mileage && !entry.odometer_photo_url;
                    const unusualMileage = entry.mileage_diff && entry.mileage_diff > 5000;
                    return (
                    <tr key={entry.id} style={{ borderBottom: '1px solid var(--border)', background: isOverdue ? 'rgba(239,68,68,0.05)' : 'transparent' }}>
                      <td style={{ padding: '10px 14px', fontSize: 13 }}>{formatDate(entry.entry_date)}</td>
                      <td style={{ padding: '10px 14px', fontSize: 13 }}><span className="badge badge-blue">{entry.entry_type}</span></td>
                      <td style={{ padding: '10px 14px', fontSize: 13 }}>
                        {entry.mileage ? `${Number(entry.mileage).toLocaleString()} km` : '—'}
                        {entry.mileage_diff ? <span style={{ color: unusualMileage ? 'var(--orange)' : 'var(--green)', fontSize: 11, marginLeft: 4 }}>(+{entry.mileage_diff}{unusualMileage ? ' ⚠' : ''})</span> : ''}
                      </td>
                      <td style={{ padding: '10px 14px', fontSize: 13 }}>{entry.driver_name || '—'}</td>
                      <td style={{ padding: '10px 14px', fontSize: 13 }}>{entry.from_location || '—'} → {entry.to_location || '—'}</td>
                      <td style={{ padding: '10px 14px', fontSize: 13 }}>{entry.purpose || '—'}</td>
                      <td style={{ padding: '10px 14px', fontSize: 13 }}>
                        <span className={`badge badge-${entry.status === 'Completed' ? 'green' : entry.status === 'Delayed' || entry.status === 'Needs review' ? 'orange' : 'gray'}`}>{entry.status}</span>
                        {isOverdue && <span className="badge badge-red" style={{ marginLeft: 4 }}>Overdue</span>}
                        {missingPhoto && <span title="Missing odometer photo" style={{ marginLeft: 4, fontSize: 12 }}>📷⚠</span>}
                      </td>
                      <td style={{ padding: '10px 14px', fontSize: 13, whiteSpace: 'nowrap' }}>
                        {canEdit && <button className="btn btn-ghost btn-sm" onClick={() => { setEditLogRecord(entry); setLogModal(true); }}><Edit2 size={13} /></button>}
                        {canDelete && <button className="btn btn-ghost btn-sm" onClick={async () => { if (!window.confirm('Delete this entry?')) return; await supabase.from('vehicle_logbook').delete().eq('id', entry.id); logbook.refetch(); }}><Trash2 size={13} /></button>}
                      </td>
                    </tr>
                  );})}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'expenses' && (() => {
        // Build combined expenses from all tabs
        const allCosts = [
          // Manual expenses
          ...expenses.data.filter(r => r.category !== 'Restoration').map(r => ({
            id: `exp-${r.id}`,
            date: r.expense_date,
            category: r.category,
            description: r.description || 'Manual expense',
            amount: Number(r.amount || 0),
            source: 'Expense',
            sourceColor: 'blue',
            supplier: r.supplier || '—',
            raw: r,            // keep the original so the row can be edited/deleted
            editable: true,
          })),
          // Insurance costs
          ...insurance.data.filter(r => r.cost).map(r => ({
            id: `ins-${r.id}`,
            date: r.start_date || r.expiry_date,
            category: 'Insurance',
            description: r.insurance_company ? `Insurance — ${r.insurance_company}` : 'Insurance',
            amount: Number(r.cost || 0),
            source: 'Insurance',
            sourceTab: 'insurance',
            sourceColor: 'yellow',
            supplier: r.insurance_company || '—',
          })),
          // Road tax costs
          ...roadTax.data.filter(r => r.amount_paid).map(r => ({
            id: `rt-${r.id}`,
            date: r.payment_date || r.expiry_date,
            category: 'Road Tax',
            description: 'Road Tax',
            amount: Number(r.amount_paid || 0),
            source: 'Road Tax',
            sourceTab: 'road-tax',
            sourceColor: 'yellow',
            supplier: '—',
          })),
          // Mechanical costs
          ...mechanical.data.filter(r => r.cost).map(r => ({
            id: `mech-${r.id}`,
            date: r.report_date,
            category: 'Mechanical',
            description: r.garage_name ? `Mechanical — ${r.garage_name}` : 'Mechanical Report',
            amount: Number(r.cost || 0),
            source: 'Mechanical',
            sourceTab: 'mechanical',
            sourceColor: 'orange',
            supplier: r.garage_name || '—',
          })),
          // Service costs
          ...services.data.filter(r => r.cost).map(r => ({
            id: `svc-${r.id}`,
            date: r.service_date,
            category: 'Service',
            description: r.garage_name ? `Service — ${r.garage_name}` : 'Service',
            amount: Number(r.cost || 0),
            source: 'Service',
            sourceTab: 'services',
            sourceColor: 'green',
            supplier: r.garage_name || '—',
          })),
        ].sort((a, b) => {
          if (!a.date) return 1;
          if (!b.date) return -1;
          return new Date(b.date) - new Date(a.date);
        });

        const grandTotal = allCosts.reduce((sum, r) => sum + r.amount, 0);

        // Breakdown by category
        const breakdown = allCosts.reduce((acc, r) => {
          acc[r.category] = (acc[r.category] || 0) + r.amount;
          return acc;
        }, {});

        return (
          <Section title="All Expenses" icon={DollarSign} onAdd={canEdit ? () => setExpModal(true) : undefined}>

            {/* Grand Total + Breakdown */}
            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 16, marginBottom: 20, alignItems: 'start' }}>
              <div style={{ background: 'var(--surface2)', borderRadius: 10, padding: '16px 24px', textAlign: 'center', border: '1px solid var(--border)' }}>
                <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 4 }}>GRAND TOTAL</div>
                <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--green)', letterSpacing: -1 }}>{formatCurrency(grandTotal)}</div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {Object.entries(breakdown).map(([cat, amount]) => (
                  <div key={cat} style={{ background: 'var(--surface2)', borderRadius: 8, padding: '10px 14px', border: '1px solid var(--border)', minWidth: 100 }}>
                    <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 2 }}>{cat}</div>
                    <div style={{ fontWeight: 700, color: 'var(--orange)' }}>{formatCurrency(amount)}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Combined Table */}
            {allCosts.length === 0 ? (
              <div className="empty-state">
                <DollarSign />
                <h3>No costs recorded yet</h3>
                <p>Costs from insurance, road tax, mechanical and service records will appear here automatically.</p>
              </div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Source</th>
                      <th>Category</th>
                      <th>Description</th>
                      <th>Amount</th>
                      <th>Supplier</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allCosts.map(r => (
                      <tr key={r.id}>
                        <td>{formatDate(r.date)}</td>
                        <td><span className={`badge badge-${r.sourceColor}`}>{r.source}</span></td>
                        <td><span className="badge badge-gray">{r.category}</span></td>
                        <td className="td-primary">
                          {r.description}
                          {(r.raw?.tags || []).length > 0 && (
                            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                              {r.raw.tags.map(t => (
                                <span key={t} style={{ padding: '1px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: 'rgba(79,142,247,0.14)', color: 'var(--accent)' }}>{t}</span>
                              ))}
                            </div>
                          )}
                        </td>
                        <td style={{ color: 'var(--orange)', fontWeight: 700 }}>
                          {formatCurrency(r.amount)}
                          {r.raw?.payment_status === 'unpaid' && (
                            <div style={{ fontSize: 10, fontWeight: 700, color: '#f97316', marginTop: 2 }}>UNPAID</div>
                          )}
                        </td>
                        <td>{r.supplier}</td>
                        <td>
                          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                            {r.editable ? (
                              <>
                                {r.raw.receipt_url && (
                                  <a href={r.raw.receipt_url} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm" title="View receipt">
                                    <ExternalLink size={12} />
                                  </a>
                                )}
                                <button className="btn btn-ghost btn-sm" title="Edit expense"
                                  onClick={() => { setEditExpRecord(r.raw); setExpCategory(null); setExpModal(true); }}>
                                  <Edit2 size={12} />
                                </button>
                                {canDelete && (
                                  <button className="btn btn-ghost btn-sm" title="Delete expense"
                                    onClick={async () => {
                                      if (!window.confirm('Delete this expense? This cannot be undone.')) return;
                                      try { await expenses.remove(r.raw.id); toast.success('Expense deleted'); }
                                      catch (err) { toast.error('Could not delete: ' + err.message); }
                                    }}>
                                    <Trash2 size={12} />
                                  </button>
                                )}
                              </>
                            ) : (
                              <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, color: 'var(--text3)' }}
                                title={`This cost comes from the ${r.source} record — edit it there`}
                                onClick={() => setActiveTab(r.sourceTab)}>
                                Edit in {r.source}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        );
      })()}

      {activeTab === 'restoration' && (() => {
        const restCosts = expenses.data
          .filter(r => r.category === 'Restoration')
          .sort((a, b) => new Date(b.expense_date || 0) - new Date(a.expense_date || 0));
        const total = restCosts.reduce((sum, r) => sum + Number(r.amount || 0), 0);
        return (
          <Section title="Restoration" icon={Wrench}
            onAdd={canEdit ? () => { setEditExpRecord(null); setExpCategory('Restoration'); setExpModal(true); } : undefined}>

            <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' }}>
              <div style={{ background: 'var(--surface2)', borderRadius: 10, padding: '16px 24px', textAlign: 'center', border: '1px solid var(--border)' }}>
                <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 4 }}>TOTAL RESTORATION SPEND</div>
                <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--accent)', letterSpacing: -1 }}>{formatCurrency(total)}</div>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text3)', maxWidth: 380, lineHeight: 1.6 }}>
                Restoration is treated as <strong>invested capital</strong>, not a running cost — it is added to
                this vehicle's invested total in Portfolio and excluded from maintenance metrics.
              </div>
            </div>

            {restCosts.length === 0 ? (
              <div className="empty-state">
                <Wrench />
                <h3>No restoration costs yet</h3>
                <p>Add an expense here to start tracking this vehicle's restoration spend.</p>
              </div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Description</th>
                      <th>Amount</th>
                      <th>Supplier</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {restCosts.map(r => (
                      <tr key={r.id}>
                        <td>{formatDate(r.expense_date)}</td>
                        <td className="td-primary">{r.description || 'Restoration work'}</td>
                        <td style={{ color: 'var(--orange)', fontWeight: 700 }}>{formatCurrency(Number(r.amount || 0))}</td>
                        <td>{r.supplier || '—'}</td>
                        <td>
                          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                            {r.receipt_url && (
                              <a href={r.receipt_url} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm" title="View receipt">
                                <ExternalLink size={12} />
                              </a>
                            )}
                            <button className="btn btn-ghost btn-sm" title="Edit"
                              onClick={() => { setEditExpRecord(r); setExpCategory(null); setExpModal(true); }}>
                              <Edit2 size={12} />
                            </button>
                            {canDelete && (
                              <button className="btn btn-ghost btn-sm" title="Delete"
                                onClick={async () => {
                                  if (!window.confirm('Delete this restoration expense? This cannot be undone.')) return;
                                  try { await expenses.remove(r.id); toast.success('Expense deleted'); }
                                  catch (err) { toast.error('Could not delete: ' + err.message); }
                                }}>
                                <Trash2 size={12} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        );
      })()}

      {activeTab === 'photos' && (
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 17 }}>Vehicle Photos</div>
              <div style={{ fontSize: 14, color: 'var(--text2)', marginTop: 4 }}>{photos.length} photo{photos.length !== 1 ? 's' : ''}</div>
            </div>
            {canEdit && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <select
                  className="form-input"
                  value={uploadAlbum}
                  onChange={e => setUploadAlbum(e.target.value)}
                  title="Album the new photos are added to"
                  style={{ width: 'auto', minWidth: 130 }}
                >
                  {PHOTO_ALBUMS.map(a => <option key={a.key} value={a.key}>Add to: {a.label}</option>)}
                </select>
                <label className="btn btn-primary" style={{ cursor: 'pointer' }}>
                  {photoUploading
                    ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Uploading…</>
                    : <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg> Add Photo</>
                  }
                  <input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={uploadPhoto} disabled={photoUploading} />
                </label>
              </div>
            )}
          </div>

          {/* Album tabs */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
            {[{ key: 'all', label: 'All', count: photos.length }, ...PHOTO_ALBUMS.map(a => ({ ...a, count: albumCount(a.key) }))].map(a => {
              const active = albumFilter === a.key;
              return (
                <button
                  key={a.key}
                  onClick={() => { setAlbumFilter(a.key); if (a.key !== 'all') setUploadAlbum(a.key); }}
                  style={{
                    padding: '6px 14px', borderRadius: 20, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                    border: `1px solid ${active ? 'var(--accent, #eab308)' : 'var(--border)'}`,
                    background: active ? 'var(--accent, #eab308)' : 'var(--surface2)',
                    color: active ? '#fff' : 'var(--text2)',
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                  }}
                >
                  {a.label}
                  <span style={{ fontSize: 11, opacity: 0.8 }}>{a.count}</span>
                </button>
              );
            })}
          </div>

          {visiblePhotos.length === 0 ? (
            <div className="empty-state">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
              <h3>{photos.length === 0 ? 'No photos yet' : `No photos in ${albumLabel(albumFilter)} yet`}</h3>
              <p>{photos.length === 0 ? 'Click "Add Photo" to upload photos of this vehicle.' : 'Choose this album in "Add to" and click "Add Photo", or move a photo here from its full-size view.'}</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
              {visiblePhotos.map(photo => (
                <div key={photo.id} style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', aspectRatio: '4/3', background: 'var(--surface2)', border: '1px solid var(--border)', cursor: 'pointer', group: true }}
                  onClick={() => setLightboxPhoto(photo)}
                >
                  <img
                    src={photo.url || photo.photo_url}
                    alt="Vehicle"
                    style={{ width: '100%', height: '100%', transition: 'transform 0.2s', ...cropStyle(photo) }}
                    onMouseEnter={e => e.target.style.transform = `scale(${(Number(photo.zoom) || 1) * 1.03})`}
                    onMouseLeave={e => e.target.style.transform = cropStyle(photo).transform || 'scale(1)'}
                  />
                  {photo.is_primary && (
                    <div style={{ position: 'absolute', top: 8, left: 8, display: 'flex', alignItems: 'center', gap: 4, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)', borderRadius: 20, padding: '3px 9px', fontSize: 10, fontWeight: 700, color: '#eab308' }}>
                      <Star size={10} fill="#eab308" /> Profile
                    </div>
                  )}
                  {albumFilter === 'all' && albumOf(photo) !== 'main' && (
                    <div style={{ position: 'absolute', bottom: 8, left: 8, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)', borderRadius: 20, padding: '3px 9px', fontSize: 10, fontWeight: 700, color: 'white' }}>
                      {albumLabel(albumOf(photo))}
                    </div>
                  )}
                  {canEdit && !photo.is_primary && <button
                    onClick={e => { e.stopPropagation(); setAsProfilePhoto(photo); }}
                    title="Set as profile photo"
                    style={{ position: 'absolute', top: 8, left: 8, display: 'flex', alignItems: 'center', gap: 4, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)', border: 'none', borderRadius: 20, padding: '4px 9px', cursor: 'pointer', color: 'white', fontSize: 10, fontWeight: 700, opacity: 0, transition: 'opacity 0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.opacity = 1}
                    onMouseLeave={e => e.currentTarget.style.opacity = 0}
                  >
                    <Star size={10} /> Set as profile
                  </button>}
                  {canDelete && <button
                    onClick={e => { e.stopPropagation(); deletePhoto(photo); }}
                    style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(239,68,68,0.9)', border: 'none', borderRadius: 6, padding: '4px 6px', cursor: 'pointer', color: 'white', opacity: 0, transition: 'opacity 0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.opacity = 1}
                    onMouseLeave={e => e.currentTarget.style.opacity = 0}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>
                  </button>}
                </div>
              ))}
            </div>
          )}

          {/* Lightbox — rendered via portal to escape stacking context */}
          {lightboxPhoto && createPortal(
            <div
              onClick={() => setLightboxPhoto(null)}
              style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
            >
              <button
                onClick={() => setLightboxPhoto(null)}
                style={{ position: 'absolute', top: 20, right: 20, background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 8, padding: '8px 12px', cursor: 'pointer', color: 'white', fontSize: 18, zIndex: 1 }}
              >✕</button>

              {visiblePhotos.length > 1 && (
                <div style={{ position: 'absolute', top: 20, left: 20, background: 'rgba(255,255,255,0.1)', borderRadius: 20, padding: '6px 14px', color: 'white', fontSize: 12, fontWeight: 600 }}>
                  {lightboxIndex + 1} / {visiblePhotos.length}
                </div>
              )}

              {visiblePhotos.length > 1 && (
                <button
                  onClick={e => { e.stopPropagation(); showPhotoAt(lightboxIndex - 1); }}
                  title="Previous photo"
                  style={{ position: 'absolute', left: 20, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: '50%', width: 44, height: 44, cursor: 'pointer', color: 'white', fontSize: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >‹</button>
              )}
              {visiblePhotos.length > 1 && (
                <button
                  onClick={e => { e.stopPropagation(); showPhotoAt(lightboxIndex + 1); }}
                  title="Next photo"
                  style={{ position: 'absolute', right: 20, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: '50%', width: 44, height: 44, cursor: 'pointer', color: 'white', fontSize: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >›</button>
              )}

              <img
                src={lightboxPhoto.url || lightboxPhoto.photo_url}
                alt="Vehicle"
                style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain', borderRadius: 12 }}
                onClick={e => e.stopPropagation()}
              />

              <div style={{ position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                {canEdit && (
                  <div onClick={e => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'rgba(255,255,255,0.08)', borderRadius: 10, padding: '8px 12px' }}>
                    <div>
                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', fontWeight: 600, marginBottom: 4 }}>Crop position</div>
                      {/* 5x5 (25% steps) rather than 3x3 (50% steps) — the coarser grid couldn't
                          land on a car that isn't near the middle or an edge, especially once
                          zoomed in, so it looked like positioning "didn't work" for photos like
                          the BMW's, where the car sits off-centre in a small part of the frame. */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 12px)', gridTemplateRows: 'repeat(5, 12px)', gap: 2, background: 'rgba(255,255,255,0.15)', padding: 2, borderRadius: 4 }}>
                        {[0, 25, 50, 75, 100].flatMap(fy => [0, 25, 50, 75, 100].map(fx => {
                          const active = (lightboxPhoto.focal_x ?? 50) === fx && (lightboxPhoto.focal_y ?? 50) === fy;
                          return (
                            <button
                              key={`${fx}-${fy}`}
                              title={`Crop anchored at ${fx}% across, ${fy}% down`}
                              onClick={() => updateCrop(lightboxPhoto, { focal_x: fx, focal_y: fy })}
                              style={{ width: 12, height: 12, borderRadius: 3, border: 'none', cursor: 'pointer', padding: 0, background: active ? '#eab308' : 'rgba(255,255,255,0.35)' }}
                            />
                          );
                        }))}
                      </div>
                    </div>
                    <div style={{ minWidth: 120 }}>
                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', fontWeight: 600, marginBottom: 4, display: 'flex', justifyContent: 'space-between' }}>
                        <span>Zoom</span><span>{(Number(lightboxPhoto.zoom) || 1).toFixed(1)}×</span>
                      </div>
                      <input
                        type="range" min="0.5" max="6" step="0.1"
                        value={Number(lightboxPhoto.zoom) || 1}
                        onChange={e => updateCrop(lightboxPhoto, { zoom: parseFloat(e.target.value) })}
                        style={{ width: '100%' }}
                        title="Above 1× zooms in on a car that's small or far away — repositioning alone can't do that. Below 1× zooms out to show the whole photo uncropped, useful if the crop is cutting off part of the car. Combine with the crop position grid to land it centred."
                      />
                    </div>
                    <div title="Preview of this photo's card crop" style={{ width: 42, height: 42, borderRadius: 6, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.3)', flexShrink: 0 }}>
                      <img
                        src={lightboxPhoto.url || lightboxPhoto.photo_url}
                        alt=""
                        style={{ width: '100%', height: '100%', ...cropStyle(lightboxPhoto) }}
                      />
                    </div>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                  {canEdit && (
                    <select
                      value={albumOf(lightboxPhoto)}
                      onClick={e => e.stopPropagation()}
                      onChange={e => moveToAlbum(lightboxPhoto, e.target.value)}
                      title="Move this photo to another album"
                      style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 8, padding: '8px 12px', color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                    >
                      {PHOTO_ALBUMS.map(a => <option key={a.key} value={a.key} style={{ color: '#111' }}>Album: {a.label}</option>)}
                    </select>
                  )}
                  {canEdit && !lightboxPhoto.is_primary && <button
                    onClick={async e => { e.stopPropagation(); await setAsProfilePhoto(lightboxPhoto); setLightboxPhoto(p => p ? { ...p, is_primary: true } : p); }}
                    style={{ background: 'rgba(234,179,8,0.9)', border: 'none', borderRadius: 8, padding: '8px 20px', cursor: 'pointer', color: 'white', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}
                  >
                    <Star size={13} /> Set as Profile Photo
                  </button>}
                  {lightboxPhoto.is_primary && (
                    <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: 8, padding: '8px 20px', color: 'white', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Star size={13} fill="white" /> Profile Photo
                    </div>
                  )}
                  {canDelete && <button
                    onClick={e => { e.stopPropagation(); deletePhoto(lightboxPhoto); setLightboxPhoto(null); }}
                    style={{ background: 'rgba(239,68,68,0.9)', border: 'none', borderRadius: 8, padding: '8px 20px', cursor: 'pointer', color: 'white', fontSize: 13, fontWeight: 600 }}
                  >
                    🗑 Delete Photo
                  </button>}
                </div>
              </div>
            </div>,
            document.body
          )}
        </div>
      )}

      {/* Edit Modal */}
      {editing && (
        <Modal title="Edit Vehicle" onClose={() => setEditing(false)} size="lg">
          <VehicleForm vehicle={vehicle} onSave={() => { setEditing(false); window.location.reload(); }} onCancel={() => setEditing(false)} />
        </Modal>
      )}

      {/* Insurance Modal */}
      {tdModal && (
        <TitleDeedModal
          vehicleId={id}
          record={editTdRecord}
          onClose={() => {
            setTdModal(false);
            setEditTdRecord(null);
            titleDeed.refetch();
            setTimeout(() => titleDeed.refetch(), 500);
          }}
        />
      )}
      {insModal && (
        <InsuranceModal
          vehicleId={id}
          record={editInsRecord}
          onClose={() => {
            setInsModal(false);
            setEditInsRecord(null);
            insurance.refetch();
            setTimeout(() => insurance.refetch(), 500);
          }}
        />
      )}
      {rtModal && (
        <RoadTaxModal
          vehicleId={id}
          record={editRtRecord}
          onClose={() => {
            setRtModal(false);
            setEditRtRecord(null);
            roadTax.refetch();
            setTimeout(() => roadTax.refetch(), 500);
          }}
        />
      )}
      {mechModal && (
        <MechanicalModal
          vehicleId={id}
          record={editMechRecord}
          onClose={() => {
            setMechModal(false);
            setEditMechRecord(null);
            mechanical.refetch();
            setTimeout(() => mechanical.refetch(), 500);
          }}
        />
      )}
      {svcModal && (
        <ServiceModal
          vehicleId={id}
          record={editSvcRecord}
          onClose={() => {
            setSvcModal(false);
            setEditSvcRecord(null);
            services.refetch();
            setTimeout(() => services.refetch(), 500);
          }}
        />
      )}
      {expModal && (
        <ExpenseModal
          vehicleId={id}
          record={editExpRecord}
          defaultCategory={expCategory}
          onClose={() => {
            setExpModal(false);
            setEditExpRecord(null);
            setExpCategory(null);
            expenses.refetch();
            setTimeout(() => expenses.refetch(), 500);
          }}
        />
      )}
      {logModal && (
        <LogbookModal
          vehicleId={id}
          record={editLogRecord}
          lastMileage={logbook.data[0]?.mileage || vehicle.mileage}
          insuranceExpired={latestIns?.expiry_date && new Date(latestIns.expiry_date) < new Date()}
          roadTaxExpired={latestRt?.expiry_date && new Date(latestRt.expiry_date) < new Date()}
          onClose={() => {
            setLogModal(false);
            setEditLogRecord(null);
            logbook.refetch();
            setTimeout(() => logbook.refetch(), 500);
          }}
        />
      )}
      {scanModal && (
        <ScanDocument
          vehicleId={id}
          vehicles={[vehicle]}
          onClose={() => setScanModal(false)}
          onSaved={() => {
            insurance.refetch(); roadTax.refetch(); mechanical.refetch(); services.refetch();
            setTimeout(() => { insurance.refetch(); roadTax.refetch(); mechanical.refetch(); services.refetch(); }, 500);
          }}
        />
      )}
    </div>
  );
};

// ── Sub-modals ────────────────────────────────────────────────────────────────
const TitleDeedModal = ({ vehicleId, onClose, record = null }) => {
  const [form, setForm] = useState({
    owner_name: record?.owner_name || '',
    issue_date: record?.issue_date || '',
    document_url: record?.document_url || '',
    document_path: record?.file_path || '',
    notes: record?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      vehicle_id: vehicleId,
      owner_name: form.owner_name || null,
      issue_date: form.issue_date || null,
      document_url: form.document_url || null,
      file_path: form.document_path || null,
      notes: form.notes || null,
    };
    let error;
    if (record) {
      ({ error } = await supabase.from('title_deeds').update(payload).eq('id', record.id));
    } else {
      ({ error } = await supabase.from('title_deeds').insert(payload).select());
    }
    if (error) { toast.error('Error: ' + error.message); setSaving(false); return; }
    if (record) await archiveReplacedFile({ vehicleId, slot: `title_deed:${record.id}`, oldUrl: record.document_url, oldPath: record.file_path, newUrl: payload.document_url, label: 'Title deed' });
    toast.success(record ? 'Title deed updated!' : 'Title deed added!');
    onClose();
    setSaving(false);
  };

  return (
    <Modal
      title={record ? 'Edit Title Deed' : 'Add Title Deed'}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" form="td-form" type="submit" disabled={saving}>
            {saving ? 'Saving…' : record ? 'Update' : 'Save'}
          </button>
        </>
      }
    >
      <form id="td-form" onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Registered Owner</label>
            <input className="form-input" value={form.owner_name} onChange={e => set('owner_name', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Issue Date</label>
            <input className="form-input" type="date" value={form.issue_date} onChange={e => set('issue_date', e.target.value)} />
          </div>
        </div>
        {form.document_url && (
          <div style={{ marginBottom: 4 }}>
            <DocumentSnippet url={form.document_url} height={130} />
          </div>
        )}
        <FileUpload
          folder="title-deed"
          label={form.document_url ? 'Upload new version (PDF/JPG/PNG) — the old one is kept' : 'Upload document (PDF/JPG/PNG)'}
          onUploaded={(r) => { set('document_url', r.url); set('document_path', r.path); }}
        />
        {record && (
          <ArchivedFiles
            vehicleId={vehicleId}
            slot={`title_deed:${record.id}`}
            current={{ url: record.document_url, path: record.file_path }}
            target={{ table: 'title_deeds', rowId: record.id, urlField: 'document_url', pathField: 'file_path' }}
            onChanged={onClose}
          />
        )}
        <div className="form-group">
          <label className="form-label">Notes</label>
          <textarea className="form-input" value={form.notes} onChange={e => set('notes', e.target.value)} />
        </div>
      </form>
    </Modal>
  );
};

const InsuranceModal = ({ vehicleId, onClose, record = null }) => {
  const [form, setForm] = useState({
    vehicle_id: vehicleId,
    insurance_company: record?.insurance_company || '',
    policy_number: record?.policy_number || '',
    start_date: record?.start_date || '',
    expiry_date: record?.expiry_date || '',
    cost: record?.cost || '',
    insurance_type: record?.insurance_type || 'Third Party',
    insured_amount: record?.insured_amount || '',
    document_url: record?.document_url || '',
    notes: record?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [fileUrl, setFileUrl] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.expiry_date) { toast.error('Please enter an expiry date'); return; }
    setSaving(true);
    const payload = {
      vehicle_id: vehicleId,
      insurance_company: form.insurance_company || null,
      policy_number: form.policy_number || null,
      start_date: form.start_date || null,
      expiry_date: form.expiry_date,
      cost: form.cost ? parseFloat(form.cost) : null,
      insurance_type: form.insurance_type || null,
      insured_amount: form.insurance_type === 'Full Comprehensive' && form.insured_amount ? parseFloat(form.insured_amount) : null,
      document_url: form.document_url || fileUrl || null,
      notes: form.notes || null,
    };
    let error;
    if (record) {
      ({ error } = await supabase.from('insurance_records').update(payload).eq('id', record.id));
    } else {
      ({ error } = await supabase.from('insurance_records').insert(payload).select());
    }
    if (error) { toast.error('Error: ' + error.message); setSaving(false); return; }
    if (record) await archiveReplacedFile({ vehicleId, slot: `insurance:${record.id}`, oldUrl: record.document_url, oldPath: null, newUrl: payload.document_url, label: 'Insurance document' });
    toast.success(record ? 'Insurance record updated!' : 'Insurance record added!');
    onClose();
    setSaving(false);
  };

  return (
    <Modal
      title={record ? 'Edit Insurance Record' : 'Add Insurance Record'}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" form="ins-form" type="submit" disabled={saving}>
            {saving ? 'Saving…' : record ? 'Update' : 'Save'}
          </button>
        </>
      }
    >
      <form id="ins-form" onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Insurance Company</label>
            <input className="form-input" value={form.insurance_company} onChange={e => set('insurance_company', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Policy Number</label>
            <input className="form-input" value={form.policy_number} onChange={e => set('policy_number', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Start Date</label>
            <input className="form-input" type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Expiry Date *</label>
            <input className="form-input" type="date" required value={form.expiry_date} onChange={e => set('expiry_date', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Cost (€)</label>
            <input className="form-input" type="number" step="0.01" value={form.cost} onChange={e => set('cost', e.target.value)} />
          </div>
          <div className="form-group">
                      <label className="form-label">Insurance Type</label>
                      <select className="form-input" value={form.insurance_type} onChange={e => set('insurance_type', e.target.value)}>
                        <option>Third Party</option>
                        <option>Third Party Fire & Theft</option>
                        <option>Full Comprehensive</option>
                        <option>Other</option>
                      </select>
                    </div>
                    {form.insurance_type === 'Full Comprehensive' && (
                      <div className="form-group">
                        <label className="form-label">Insured Amount (€)</label>
                        <input className="form-input" type="number" step="0.01" value={form.insured_amount} onChange={e => set('insured_amount', e.target.value)} placeholder="e.g. 15000.00" />
                      </div>
                    )}
        </div>
        <FileUpload folder="insurance" onUploaded={(r) => { setFileUrl(r.url); set('document_url', r.url); }} label="Upload insurance document" />
        {record && (
          <ArchivedFiles
            vehicleId={vehicleId}
            slot={`insurance:${record.id}`}
            current={{ url: record.document_url, path: null }}
            target={{ table: 'insurance_records', rowId: record.id, urlField: 'document_url' }}
            onChanged={onClose}
          />
        )}
        <div className="form-group">
          <label className="form-label">Notes</label>
          <textarea className="form-input" value={form.notes} onChange={e => set('notes', e.target.value)} />
        </div>
      </form>
    </Modal>
  );
};

const LogbookModal = ({ vehicleId, onClose, record = null, lastMileage, insuranceExpired, roadTaxExpired }) => {
  const ENTRY_TYPES = ['Mileage','Trip','Driver Assignment','Storage Update','Service','Maintenance','Inspection','Detailing','Event','Fuel','Incident','General Note'];
  const STATUSES = ['Scheduled','In Progress','Completed','Delayed','Cancelled','Awaiting Invoice','Awaiting Return','Needs Review'];
  const [form, setForm] = useState({
    entry_date: record?.entry_date ? record.entry_date.slice(0,16) : new Date().toISOString().slice(0,16),
    entry_type: record?.entry_type || 'Trip',
    odometer_photo_url: record?.odometer_photo_url || '',
    invoice_url: record?.invoice_url || '',
    mileage: record?.mileage || '',
    driver_name: record?.driver_name || '',
    driver_contact: record?.driver_contact || '',
    assigned_by: record?.assigned_by || '',
    from_location: record?.from_location || '',
    to_location: record?.to_location || '',
    storage_location: record?.storage_location || '',
    purpose: record?.purpose || '',
    expected_departure: record?.expected_departure ? record.expected_departure.slice(0,16) : '',
    expected_return: record?.expected_return ? record.expected_return.slice(0,16) : '',
    actual_return: record?.actual_return ? record.actual_return.slice(0,16) : '',
    status: record?.status || 'Completed',
    fuel_level: record?.fuel_level || '',
    battery_status: record?.battery_status || '',
    exterior_condition: record?.exterior_condition || '',
    interior_condition: record?.interior_condition || '',
    damage_notes: record?.damage_notes || '',
    notes: record?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const newMileage = form.mileage ? parseInt(form.mileage) : null;
    const prevMileage = lastMileage ? Number(lastMileage) : null;
    const mileageDiff = newMileage && prevMileage ? newMileage - prevMileage : null;

    const payload = {
      vehicle_id: vehicleId,
      entry_date: form.entry_date || new Date().toISOString(),
      entry_type: form.entry_type,
      odometer_photo_url: form.odometer_photo_url || null,
      invoice_url: form.invoice_url || null,
      mileage: newMileage,
      previous_mileage: prevMileage,
      mileage_diff: mileageDiff,
      driver_name: form.driver_name || null,
      driver_contact: form.driver_contact || null,
      assigned_by: form.assigned_by || null,
      from_location: form.from_location || null,
      to_location: form.to_location || null,
      storage_location: form.storage_location || null,
      purpose: form.purpose || null,
      expected_departure: form.expected_departure || null,
      expected_return: form.expected_return || null,
      actual_return: form.actual_return || null,
      status: form.status,
      fuel_level: form.fuel_level || null,
      battery_status: form.battery_status || null,
      exterior_condition: form.exterior_condition || null,
      interior_condition: form.interior_condition || null,
      damage_notes: form.damage_notes || null,
      notes: form.notes || null,
    };

    let error;
    if (record) {
      ({ error } = await supabase.from('vehicle_logbook').update(payload).eq('id', record.id));
    } else {
      const { data: { user } } = await supabase.auth.getUser();
      payload.created_by = user?.id || null;
      ({ error } = await supabase.from('vehicle_logbook').insert(payload).select());
    }
    if (error) { toast.error('Error: ' + error.message); setSaving(false); return; }
    if (record) await archiveReplacedFile({ vehicleId, slot: `logbook_invoice:${record.id}`, oldUrl: record.invoice_url, oldPath: null, newUrl: payload.invoice_url, label: 'Logbook invoice' });
    toast.success(record ? 'Logbook entry updated!' : 'Logbook entry added!');
    onClose();
    setSaving(false);
  };

  return (
    <Modal
      title={record ? 'Edit Logbook Entry' : 'Add Logbook Entry'}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" form="log-form" type="submit" disabled={saving}>
            {saving ? 'Saving…' : record ? 'Update' : 'Save'}
          </button>
        </>
      }
    >
      <form id="log-form" onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {(insuranceExpired || roadTaxExpired) && !record && (
          <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: 'var(--red)' }}>
            ⚠️ Warning: {insuranceExpired ? 'Insurance has expired. ' : ''}{roadTaxExpired ? 'Road tax has expired. ' : ''}Creating a movement entry for this vehicle is not recommended.
          </div>
        )}
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Date & Time</label>
            <input className="form-input" type="datetime-local" value={form.entry_date} onChange={e => set('entry_date', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Entry Type</label>
            <select className="form-input" value={form.entry_type} onChange={e => set('entry_type', e.target.value)}>
              {ENTRY_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Current Mileage (km)</label>
            <input className="form-input" type="number" value={form.mileage} onChange={e => set('mileage', e.target.value)} placeholder={lastMileage ? `Last: ${Number(lastMileage).toLocaleString()} km` : ''} />
          </div>
          <div className="form-group">
            <label className="form-label">Status</label>
            <select className="form-input" value={form.status} onChange={e => set('status', e.target.value)}>
              {STATUSES.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Driver Name</label>
            <input className="form-input" value={form.driver_name} onChange={e => set('driver_name', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Driver Contact</label>
            <input className="form-input" value={form.driver_contact} onChange={e => set('driver_contact', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">From Location</label>
            <input className="form-input" value={form.from_location} onChange={e => set('from_location', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">To Location</label>
            <input className="form-input" value={form.to_location} onChange={e => set('to_location', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Current Storage Location</label>
            <input className="form-input" value={form.storage_location} onChange={e => set('storage_location', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Purpose</label>
            <input className="form-input" value={form.purpose} onChange={e => set('purpose', e.target.value)} placeholder="e.g. Service, Event, Test drive" />
          </div>
          <div className="form-group">
            <label className="form-label">Expected Departure</label>
            <input className="form-input" type="datetime-local" value={form.expected_departure} onChange={e => set('expected_departure', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Expected Return</label>
            <input className="form-input" type="datetime-local" value={form.expected_return} onChange={e => set('expected_return', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Actual Return</label>
            <input className="form-input" type="datetime-local" value={form.actual_return} onChange={e => set('actual_return', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Fuel Level</label>
            <input className="form-input" value={form.fuel_level} onChange={e => set('fuel_level', e.target.value)} placeholder="e.g. Full, 3/4, 1/2" />
          </div>
          <div className="form-group">
            <label className="form-label">Battery Status</label>
            <input className="form-input" value={form.battery_status} onChange={e => set('battery_status', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Exterior Condition</label>
            <input className="form-input" value={form.exterior_condition} onChange={e => set('exterior_condition', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Interior Condition</label>
            <input className="form-input" value={form.interior_condition} onChange={e => set('interior_condition', e.target.value)} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Damage Notes</label>
          <textarea className="form-input" value={form.damage_notes} onChange={e => set('damage_notes', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Notes</label>
          <textarea className="form-input" value={form.notes} onChange={e => set('notes', e.target.value)} />
        </div>
      </form>
    </Modal>
  );
};

const RoadTaxModal = ({ vehicleId, onClose, record = null }) => {
  const [form, setForm] = useState({
    vehicle_id: vehicleId,
    payment_date: record?.payment_date || '',
    expiry_date: record?.expiry_date || '',
    amount_paid: record?.amount_paid || '',
    receipt_url: record?.receipt_url || '',
    notes: record?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [fileUrl, setFileUrl] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.expiry_date) { toast.error('Please enter an expiry date'); return; }
    setSaving(true);
    const payload = {
      vehicle_id: vehicleId,
      payment_date: form.payment_date || null,
      expiry_date: form.expiry_date,
      amount_paid: form.amount_paid ? parseFloat(form.amount_paid) : null,
      receipt_url: form.receipt_url || fileUrl || null,
      notes: form.notes || null,
    };
    let error;
    if (record) {
      ({ error } = await supabase.from('road_tax_records').update(payload).eq('id', record.id));
    } else {
      ({ error } = await supabase.from('road_tax_records').insert(payload).select());
    }
    if (error) { toast.error('Error: ' + error.message); setSaving(false); return; }
    if (record) await archiveReplacedFile({ vehicleId, slot: `road_tax:${record.id}`, oldUrl: record.receipt_url, oldPath: null, newUrl: payload.receipt_url, label: 'Road tax receipt' });
    toast.success(record ? 'Road tax record updated!' : 'Road tax record added!');
    onClose();
    setSaving(false);
  };

  return (
    <Modal
      title={record ? 'Edit Road Tax Record' : 'Add Road Tax Record'}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" form="rt-form" type="submit" disabled={saving}>
            {saving ? 'Saving…' : record ? 'Update' : 'Save'}
          </button>
        </>
      }
    >
      <form id="rt-form" onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Payment Date</label>
            <input className="form-input" type="date" value={form.payment_date} onChange={e => set('payment_date', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Expiry Date *</label>
            <input className="form-input" type="date" required value={form.expiry_date} onChange={e => set('expiry_date', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Amount Paid (€)</label>
            <input className="form-input" type="number" step="0.01" value={form.amount_paid} onChange={e => set('amount_paid', e.target.value)} />
          </div>
        </div>
        <FileUpload folder="road-tax" onUploaded={(r) => { setFileUrl(r.url); set('receipt_url', r.url); }} label="Upload receipt" />
        {record && (
          <ArchivedFiles
            vehicleId={vehicleId}
            slot={`road_tax:${record.id}`}
            current={{ url: record.receipt_url, path: null }}
            target={{ table: 'road_tax_records', rowId: record.id, urlField: 'receipt_url' }}
            onChanged={onClose}
          />
        )}
        <div className="form-group">
          <label className="form-label">Notes</label>
          <textarea className="form-input" value={form.notes} onChange={e => set('notes', e.target.value)} />
        </div>
        <FileUpload folder="logbook" onUploaded={(r) => set('odometer_photo_url', r.url)} label="Upload odometer photo" />
        <FileUpload folder="logbook" onUploaded={(r) => set('invoice_url', r.url)} label="Upload invoice / service document" />
        {record && (
          <ArchivedFiles
            vehicleId={vehicleId}
            slot={`logbook_invoice:${record.id}`}
            current={{ url: record.invoice_url, path: null }}
            target={{ table: 'vehicle_logbook', rowId: record.id, urlField: 'invoice_url' }}
            onChanged={onClose}
          />
        )}
      </form>
    </Modal>
  );
};

const MechanicalModal = ({ vehicleId, onClose, record = null }) => {
  const [form, setForm] = useState({
    vehicle_id: vehicleId,
    report_date: record?.report_date || '',
    next_due_date: record?.next_due_date || '',
    garage_name: record?.garage_name || '',
    mechanic_name: record?.mechanic_name || '',
    result: record?.result || 'Passed',
    cost: record?.cost || '',
    document_url: record?.document_url || '',
    notes: record?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [fileUrl, setFileUrl] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      vehicle_id: vehicleId,
      report_date: form.report_date || null,
      next_due_date: form.next_due_date || null,
      garage_name: form.garage_name || null,
      mechanic_name: form.mechanic_name || null,
      result: form.result,
      cost: form.cost ? parseFloat(form.cost) : null,
      document_url: form.document_url || fileUrl || null,
      notes: form.notes || null,
    };
    let error;
    if (record) {
      ({ error } = await supabase.from('mechanical_reports').update(payload).eq('id', record.id));
    } else {
      ({ error } = await supabase.from('mechanical_reports').insert(payload).select());
    }
    if (error) { toast.error('Error: ' + error.message); setSaving(false); return; }
    if (record) await archiveReplacedFile({ vehicleId, slot: `mechanical:${record.id}`, oldUrl: record.document_url, oldPath: null, newUrl: payload.document_url, label: 'Mechanical report' });
    toast.success(record ? 'Report updated!' : 'Report added!');
    onClose();
    setSaving(false);
  };

  return (
    <Modal
      title={record ? 'Edit Mechanical Report' : 'Add Mechanical Report'}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" form="mech-form" type="submit" disabled={saving}>
            {saving ? 'Saving…' : record ? 'Update' : 'Save'}
          </button>
        </>
      }
    >
      <form id="mech-form" onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Report Date</label>
            <input className="form-input" type="date" value={form.report_date} onChange={e => set('report_date', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Next Due Date</label>
            <input className="form-input" type="date" value={form.next_due_date} onChange={e => set('next_due_date', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Garage Name</label>
            <input className="form-input" value={form.garage_name} onChange={e => set('garage_name', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Mechanic Name</label>
            <input className="form-input" value={form.mechanic_name} onChange={e => set('mechanic_name', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Result</label>
            <select className="form-input" value={form.result} onChange={e => set('result', e.target.value)}>
              <option>Passed</option>
              <option>Failed</option>
              <option>Needs Repair</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Cost (€)</label>
            <input className="form-input" type="number" step="0.01" value={form.cost} onChange={e => set('cost', e.target.value)} placeholder="0.00" />
          </div>
        </div>
        <FileUpload folder="mechanical" onUploaded={(r) => { setFileUrl(r.url); set('document_url', r.url); }} label="Upload report document" />
        {record && (
          <ArchivedFiles
            vehicleId={vehicleId}
            slot={`mechanical:${record.id}`}
            current={{ url: record.document_url, path: null }}
            target={{ table: 'mechanical_reports', rowId: record.id, urlField: 'document_url' }}
            onChanged={onClose}
          />
        )}
        <div className="form-group">
          <label className="form-label">Notes</label>
          <textarea className="form-input" value={form.notes} onChange={e => set('notes', e.target.value)} />
        </div>
      </form>
    </Modal>
  );
};

const ServiceModal = ({ vehicleId, onClose, record = null }) => {
  const [form, setForm] = useState({
    vehicle_id: vehicleId,
    service_date: record?.service_date || '',
    mileage_at_service: record?.mileage_at_service || '',
    garage_name: record?.garage_name || '',
    work_carried_out: record?.work_carried_out || '',
    parts_replaced: record?.parts_replaced || '',
    cost: record?.cost || '',
    invoice_url: record?.invoice_url || '',
    next_service_date: record?.next_service_date || '',
    next_service_mileage: record?.next_service_mileage || '',
    notes: record?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [fileUrl, setFileUrl] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      vehicle_id: vehicleId,
      service_date: form.service_date || null,
      mileage_at_service: form.mileage_at_service ? parseInt(form.mileage_at_service) : null,
      garage_name: form.garage_name || null,
      work_carried_out: form.work_carried_out || null,
      parts_replaced: form.parts_replaced || null,
      cost: form.cost ? parseFloat(form.cost) : null,
      invoice_url: form.invoice_url || fileUrl || null,
      next_service_date: form.next_service_date || null,
      next_service_mileage: form.next_service_mileage ? parseInt(form.next_service_mileage) : null,
      notes: form.notes || null,
    };
    let error;
    if (record) {
      ({ error } = await supabase.from('service_records').update(payload).eq('id', record.id));
    } else {
      ({ error } = await supabase.from('service_records').insert(payload).select());
    }
    if (error) { toast.error('Error: ' + error.message); setSaving(false); return; }
    if (record) await archiveReplacedFile({ vehicleId, slot: `service:${record.id}`, oldUrl: record.invoice_url, oldPath: null, newUrl: payload.invoice_url, label: 'Service invoice' });
    toast.success(record ? 'Service record updated!' : 'Service record added!');
    onClose();
    setSaving(false);
  };

  return (
    <Modal
      title={record ? 'Edit Service Record' : 'Add Service Record'}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" form="svc-form" type="submit" disabled={saving}>
            {saving ? 'Saving…' : record ? 'Update' : 'Save'}
          </button>
        </>
      }
    >
      <form id="svc-form" onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Service Date</label>
            <input className="form-input" type="date" value={form.service_date} onChange={e => set('service_date', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Mileage at Service</label>
            <input className="form-input" type="number" value={form.mileage_at_service} onChange={e => set('mileage_at_service', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Garage Name</label>
            <input className="form-input" value={form.garage_name} onChange={e => set('garage_name', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Cost (€)</label>
            <input className="form-input" type="number" step="0.01" value={form.cost} onChange={e => set('cost', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Next Service Date</label>
            <input className="form-input" type="date" value={form.next_service_date} onChange={e => set('next_service_date', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Next Service Mileage</label>
            <input className="form-input" type="number" value={form.next_service_mileage} onChange={e => set('next_service_mileage', e.target.value)} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Work Carried Out</label>
          <textarea className="form-input" value={form.work_carried_out} onChange={e => set('work_carried_out', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Parts Replaced</label>
          <textarea className="form-input" value={form.parts_replaced} onChange={e => set('parts_replaced', e.target.value)} />
        </div>
        <FileUpload folder="services" onUploaded={(r) => { setFileUrl(r.url); set('invoice_url', r.url); }} label="Upload invoice" />
        {record && (
          <ArchivedFiles
            vehicleId={vehicleId}
            slot={`service:${record.id}`}
            current={{ url: record.invoice_url, path: null }}
            target={{ table: 'service_records', rowId: record.id, urlField: 'invoice_url' }}
            onChanged={onClose}
          />
        )}
        <div className="form-group">
          <label className="form-label">Notes</label>
          <textarea className="form-input" value={form.notes} onChange={e => set('notes', e.target.value)} />
        </div>
      </form>
    </Modal>
  );
};


// ── Overview presentational helpers (module scope: stable identity) ──────────
const OvHead = ({ title, hint, actionLabel, onAction }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
    <div>
      <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: -0.2 }}>{title}</div>
      {hint && <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>{hint}</div>}
    </div>
    {actionLabel && (
      <button className="btn btn-ghost btn-sm" style={{ flexShrink: 0, fontSize: 12 }} onClick={onAction}>
        {actionLabel}
      </button>
    )}
  </div>
);

const OvMetric = ({ label, value, note, tone, noteTone }) => (
  <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px' }}>
    <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{label}</div>
    <div style={{ fontSize: 18, fontWeight: 800, color: tone || 'var(--text)', letterSpacing: -0.4 }}>{value}</div>
    {note && <div style={{ fontSize: 10.5, color: noteTone || 'var(--text3)', marginTop: 4 }}>{note}</div>}
  </div>
);

const OvList = ({ title, rows, empty, onSeeAll }) => (
  <div>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
      <div style={{ fontSize: 12, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{title}</div>
      {rows.length > 0 && (
        <button onClick={onSeeAll}
          style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: 11, cursor: 'pointer', padding: 0 }}>
          See all
        </button>
      )}
    </div>
    {rows.length === 0 ? (
      <div style={{ fontSize: 12.5, color: 'var(--text3)', padding: '10px 0' }}>{empty}</div>
    ) : rows.map(r => (
      <div key={r.key} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'baseline',
                                padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.left}</div>
          <div style={{ fontSize: 11, color: 'var(--text3)' }}>{r.sub}</div>
        </div>
        <div style={{ fontSize: 13, fontWeight: 600, flexShrink: 0 }}>{r.right}</div>
      </div>
    ))}
  </div>
);
